import type { S3Client } from "@aws-sdk/client-s3";
import {
  CopyObjectCommand,
  DeleteObjectCommand,
  HeadObjectCommand,
} from "@aws-sdk/client-s3";

import {
  APPROVED_QUALITY_REPORTS_FOLDER_NAME,
  EVIDO_INTERNAL_FOLDER_NAME,
  FOR_APPROVAL_FOLDER_NAME,
  RESULTS_FOLDER_NAME,
  moveToProjectFolder,
  segmentsBelowProject,
} from "@/storage/folderNames";
import { baseName, isQualityReport, qualityReportKeys } from "./qualityReports";

/** The `results/` key a `for_approval/` candidate is promoted to. */
export function approvedResultKey(forApprovalKey: string): string {
  return moveToProjectFolder(forApprovalKey, RESULTS_FOLDER_NAME);
}

export function approvedQualityReportKey(reportKey: string): string {
  return moveToProjectFolder(
    reportKey,
    `${EVIDO_INTERNAL_FOLDER_NAME}/${APPROVED_QUALITY_REPORTS_FOLDER_NAME}`,
  );
}

function assertCandidateKey(forApprovalKey: string): void {
  if (segmentsBelowProject(forApprovalKey)[0] !== FOR_APPROVAL_FOLDER_NAME) {
    throw new Error(`${forApprovalKey} is not awaiting approval.`);
  }
  if (isQualityReport(forApprovalKey)) {
    throw new Error(
      `${baseName(forApprovalKey)} is a data quality report; act on its result instead.`,
    );
  }
}

// S3 `CopySource` is `bucket/key`; the key must be URI-encoded while keeping the
// `/` separators intact.
function encodeCopySource(bucket: string, key: string): string {
  const encodedKey = key.split("/").map(encodeURIComponent).join("/");
  return `${bucket}/${encodedKey}`;
}

/** Whether an object exists at `key`. Treats a 404 (NotFound) as "absent". */
async function objectExists(
  client: S3Client,
  bucket: string,
  key: string,
): Promise<boolean> {
  try {
    await client.send(new HeadObjectCommand({ Bucket: bucket, Key: key }));
    return true;
  } catch (error: unknown) {
    if (
      typeof error === "object" &&
      error !== null &&
      "$metadata" in error &&
      (error as { $metadata?: { httpStatusCode?: number } }).$metadata
        ?.httpStatusCode === 404
    ) {
      return false;
    }
    throw error;
  }
}

async function findQualityReports(
  client: S3Client,
  bucket: string,
  forApprovalKey: string,
): Promise<string[]> {
  const candidateKeys = qualityReportKeys(forApprovalKey);
  const exists = await Promise.all(
    candidateKeys.map((key) => objectExists(client, bucket, key)),
  );
  return candidateKeys.filter((_key, index) => exists[index]);
}

async function copyObject(
  client: S3Client,
  bucket: string,
  sourceKey: string,
  destinationKey: string,
): Promise<void> {
  await client.send(
    new CopyObjectCommand({
      Bucket: bucket,
      Key: destinationKey,
      CopySource: encodeCopySource(bucket, sourceKey),
    }),
  );
}

async function deleteObjects(
  client: S3Client,
  bucket: string,
  keys: readonly string[],
): Promise<void> {
  await Promise.all(
    keys.map((key) =>
      client.send(new DeleteObjectCommand({ Bucket: bucket, Key: key })),
    ),
  );
}

/** Copies everything before deleting anything, so a failed copy can simply be retried. */
export async function approveByMoving(
  client: S3Client,
  bucket: string,
  forApprovalKey: string,
): Promise<string> {
  assertCandidateKey(forApprovalKey);
  const reportKeys = await findQualityReports(client, bucket, forApprovalKey);
  if (reportKeys.length === 0) {
    throw new Error(
      `No data quality report uploaded for ${baseName(forApprovalKey)}.`,
    );
  }
  const destinationKey = approvedResultKey(forApprovalKey);
  if (await objectExists(client, bucket, destinationKey)) {
    throw new Error(
      `A released result already exists at ${destinationKey}; refusing to overwrite it.`,
    );
  }
  await Promise.all(
    reportKeys.map((reportKey) =>
      copyObject(
        client,
        bucket,
        reportKey,
        approvedQualityReportKey(reportKey),
      ),
    ),
  );
  await copyObject(client, bucket, forApprovalKey, destinationKey);
  await deleteObjects(client, bucket, [forApprovalKey, ...reportKeys]);
  return destinationKey;
}

export async function rejectByDeletion(
  client: S3Client,
  bucket: string,
  forApprovalKey: string,
): Promise<string> {
  assertCandidateKey(forApprovalKey);
  const reportKeys = await findQualityReports(client, bucket, forApprovalKey);
  await deleteObjects(client, bucket, [forApprovalKey, ...reportKeys]);
  return forApprovalKey;
}
